import type { ActionReturnType } from '../../types';
import { ManagementProgress } from '../../../types';

import {
  CUSTOM_BG_CACHE_NAME,
  LANG_CACHE_NAME,
  LOCK_SCREEN_ANIMATION_DURATION_MS,
  MEDIA_CACHE_NAME,
  MEDIA_CACHE_NAME_AVATARS,
  MEDIA_PROGRESSIVE_CACHE_NAME,
  MULTIACCOUNT_MAX_SLOTS,
} from '../../../config';
import { updateAppBadge } from '../../../util/appBadge';
import { toCredentialRequestOptions } from '../../../util/browser/passkeys';
import {
  IS_WEBAUTHN_SUPPORTED,
  IS_WEBM_SUPPORTED, MAX_BUFFER_SIZE, PLATFORM_ENV,
} from '../../../util/browser/windowEnvironment';
import * as cacheApi from '../../../util/cacheApi';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { getIsMiniAppAuthPending, savePendingMiniAppAuth } from '../../../util/miniApp';
import {
  ACCOUNT_SLOT, getAccountsInfo, getAccountsInfoAsync, getAccountSlotUrl, getFirstLoggedInAccountSlot,
} from '../../../util/multiaccount';
import { unsubscribe } from '../../../util/notifications';
import { clearEncryptedSession, encryptSession, forgetPasscode } from '../../../util/passcode';
import {
  getInitialLocationHash, parseInitialLocationHash, resetInitialLocationHash, resetLocationHash,
} from '../../../util/routing';
import { pause } from '../../../util/schedulers';
import {
  clearStoredSession,
  loadStoredSession,
  loadSlotSession,
  storeSession,
} from '../../../util/sessions';
import { forceWebsync } from '../../../util/websync';
import { initializeStorage, getStorageAdapter, IS_CLOUD_STORAGE_ENABLED } from '../../../util/storage';
import { migrateToCloudStorage, shouldMigrate, cleanupOldData } from '../../../util/storage/migration';
import {
  callApi, callApiLocal, initApi, setShouldEnableDebugLog,
} from '../../../api/gramjs';
import {
  removeGlobalFromCache, removeSharedStateFromCache, serializeGlobal, serializeShared,
} from '../../cache';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import {
  clearGlobalForLockScreen, updateManagementProgress, updatePasscodeSettings,
} from '../../reducers';
import { updateAuth } from '../../reducers/auth';
import { selectSharedSettings } from '../../selectors/sharedState';
import { destroySharedStatePort } from '../../shared/sharedStateConnector';

let resetStoragePromise: Promise<boolean> | undefined;

addActionHandler('initApi', async (global, actions): Promise<void> => {
  // Ждём токен Mini App: без него авторизация уйдёт в обычный флоу и токен будет потерян
  if (getIsMiniAppAuthPending()) {
    // eslint-disable-next-line no-console
    console.log('[initApi] Skipped, Mini App token is still being fetched');
    return;
  }

  // Вход по токену уже запущен, а сам токен израсходован первым вызовом. Повторный
  // вызов (его шлёт `switchMultitabRole`, когда вкладка становится мастером) поднял бы
  // в воркере второй клиент поверх уже авторизованного - чаты после этого не грузятся.
  // Флаг снимается в apiUpdaters на любом исходе авторизации, залипнуть не может
  if (global.auth?.isFetchingMiniAppToken && !global.auth?.pendingMiniAppToken) {
    // eslint-disable-next-line no-console
    console.log('[initApi] Skipped, Mini App token login is already in progress');
    return;
  }

  // 1. Initialize storage adapter
  await initializeStorage();

  // 2. Check for and run migration if needed
  // Миграция уносит данные в CloudStorage, а `cleanupOldData` следом стирает их
  // из localStorage - при выключенном облаке это молча выкинуло бы все сессии
  if (IS_CLOUD_STORAGE_ENABLED) {
    try {
      const adapter = getStorageAdapter();
      if (await shouldMigrate(adapter)) {
        // eslint-disable-next-line no-console
        console.log('[initApi] Starting CloudStorage migration');
        const result = await migrateToCloudStorage(adapter);
        if (result.success) {
          // eslint-disable-next-line no-console
          console.log('[initApi] Migration completed successfully');
        } else {
          // eslint-disable-next-line no-console
          console.error('[initApi] Migration failed:', result.error);
        }
      }

      // 3. Cleanup old data if needed
      if (cleanupOldData) {
        await cleanupOldData();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[initApi] Storage initialization/migration failed', error);
      // Continue with localStorage fallback
    }
  }

  const initialLocationHash = parseInitialLocationHash();
  const {
    shouldAllowHttpTransport,
    shouldForceHttpTransport,
    shouldDebugExportedSenders,
    shouldCollectDebugLogs,
    language,
  } = selectSharedSettings(global);

  // Проверить Mini App токен из global state
  const miniAppToken = global.auth?.pendingMiniAppToken;
  const miniAppDcId = global.auth?.pendingMiniAppDcId;
  const miniAppUserId = global.auth?.pendingMiniAppUserId;

  const hasTestParam = window.location.search.includes('test') || initialLocationHash?.tgWebAuthTest === '1';

  const isTestServer = global.config?.isTestServer;
  const accountsInfo = await getAccountsInfoAsync();
  const accountIds = Object.values(accountsInfo)
    .filter((info) => info.isTest === isTestServer)
    .map(({ userId }) => userId)
    .filter(Boolean);

  // Если Mini App пытается авторизовать уже существующий аккаунт - редиректим на него БЕЗ хэша
  if (miniAppToken && miniAppUserId) {
    const existingSlot = Object.entries(accountsInfo)
      .find(([_, info]) => info.userId === miniAppUserId)?.[0];

    if (existingSlot) {
      const existingSlotNumber = Number(existingSlot);
      const currentSlot = ACCOUNT_SLOT || 1;

      // Verify that the slot actually has a valid session (not just meta)
      const slotSession = await loadSlotSession(existingSlotNumber);
      const hasValidSession = slotSession && slotSession.dcId && Object.keys(slotSession).some((key) => key.includes('auth_key'));

      // eslint-disable-next-line no-console
      console.log(
        '[initApi] Mini App user already authorized.',
        'existingSlot:', existingSlot,
        'currentSlot:', currentSlot,
        'hasValidSession:', hasValidSession,
      );

      if (!hasValidSession) {
        // Meta exists but no valid session - proceed with fresh login
        // eslint-disable-next-line no-console
        console.log('[initApi] Existing slot has no valid session, proceeding with login');
        // Don't redirect, continue with login flow below
      } else if (existingSlotNumber !== currentSlot) {
        // Редирект на существующий слот БЕЗ хэша (это остановит цикл)
        // eslint-disable-next-line no-console
        console.log('[initApi] Redirecting to existing slot WITHOUT hash');
        window.location.replace(getAccountSlotUrl(existingSlotNumber));
        return;
      } else {
        // Уже в правильном слоте и есть валидная сессия - очистить Mini App токен и использовать существующую сессию
        // eslint-disable-next-line no-console
        console.log('[initApi] Already in correct slot, clearing Mini App token');
        setGlobal({
          ...getGlobal(),
          auth: {
            ...getGlobal().auth,
            pendingMiniAppToken: undefined,
            pendingMiniAppDcId: undefined,
            pendingMiniAppUserId: undefined,
            isFetchingMiniAppToken: false,
          },
        });
        // Продолжить без Mini App токена
        void initApi(actions.apiUpdate, {
          userAgent: navigator.userAgent,
          platform: PLATFORM_ENV,
          sessionData: await loadStoredSession(),
          isWebmSupported: IS_WEBM_SUPPORTED,
          maxBufferSize: MAX_BUFFER_SIZE,
          webAuthToken: initialLocationHash?.tgWebAuthToken,
          dcId: initialLocationHash?.tgWebAuthDcId ? Number(initialLocationHash?.tgWebAuthDcId) : undefined,
          mockScenario: initialLocationHash?.mockScenario,
          shouldAllowHttpTransport,
          shouldForceHttpTransport,
          shouldDebugExportedSenders,
          langCode: language,
          isTestServerRequested: hasTestParam,
          accountIds,
          hasPasskeySupport: IS_WEBAUTHN_SUPPORTED,
        });
        void setShouldEnableDebugLog(Boolean(shouldCollectDebugLogs));
        return;
      }
    }
  }

  let storedSession = await loadStoredSession();
  const hasActiveSession = storedSession && Object.keys(storedSession.keys || {}).length > 0;
  const hasWebAuthToken = Boolean(initialLocationHash?.tgWebAuthToken || miniAppToken);

  // eslint-disable-next-line no-console
  console.log(
    '[initApi] ACCOUNT_SLOT:', ACCOUNT_SLOT,
    'hasActiveSession:', hasActiveSession,
    'hasWebAuthToken:', hasWebAuthToken,
    'token:', initialLocationHash?.tgWebAuthToken?.substring(0, 20),
  );

  if (hasActiveSession && hasWebAuthToken) {
    const occupiedSlots = new Set(Object.keys(accountsInfo).map(Number));
    let freeSlot: number | undefined;

    for (let slot = 1; slot <= MULTIACCOUNT_MAX_SLOTS; slot++) {
      if (!occupiedSlots.has(slot) && slot !== (ACCOUNT_SLOT || 1)) {
        freeSlot = slot;
        break;
      }
    }

    // eslint-disable-next-line no-console
    console.log('[initApi] Found free slot:', freeSlot, 'occupied:', Array.from(occupiedSlots));

    if (freeSlot) {
      // Токен уже выпущен - переносим его на новую страницу, чтобы не просить у бэкенда второй
      if (miniAppToken) {
        savePendingMiniAppAuth({ token: miniAppToken, dcId: miniAppDcId, userId: miniAppUserId });
      }

      const url = getAccountSlotUrl(freeSlot);
      const newUrl = new URL(url);
      const originalHash = getInitialLocationHash();
      newUrl.hash = originalHash;
      // eslint-disable-next-line no-console
      console.log('[initApi] Redirecting to:', newUrl.toString(), 'with hash:', originalHash);
      window.location.replace(newUrl.toString());
      return;
    }

    // eslint-disable-next-line no-console
    console.log('[initApi] No free slots, clearing current session');
    await clearStoredSession(ACCOUNT_SLOT);
    storedSession = await loadStoredSession();
  }

  void initApi(actions.apiUpdate, {
    userAgent: navigator.userAgent,
    platform: PLATFORM_ENV,
    sessionData: storedSession,
    isWebmSupported: IS_WEBM_SUPPORTED,
    maxBufferSize: MAX_BUFFER_SIZE,
    webAuthToken: miniAppToken || initialLocationHash?.tgWebAuthToken,
    dcId: miniAppDcId || (initialLocationHash?.tgWebAuthDcId ? Number(initialLocationHash?.tgWebAuthDcId) : undefined),
    mockScenario: initialLocationHash?.mockScenario,
    shouldAllowHttpTransport,
    shouldForceHttpTransport,
    shouldDebugExportedSenders,
    langCode: language,
    isTestServerRequested: hasTestParam,
    accountIds,
    hasPasskeySupport: IS_WEBAUTHN_SUPPORTED,
  });

  // Очистить Mini App токен после использования
  if (miniAppToken) {
    setGlobal({
      ...getGlobal(),
      auth: {
        ...getGlobal().auth,
        pendingMiniAppToken: undefined,
        pendingMiniAppDcId: undefined,
        pendingMiniAppUserId: undefined,
      },
    });
  }

  void setShouldEnableDebugLog(Boolean(shouldCollectDebugLogs));
});

addActionHandler('setAuthPhoneNumber', (global, actions, payload): ActionReturnType => {
  const { phoneNumber } = payload;

  void callApi('provideAuthPhoneNumber', phoneNumber.replace(/[^\d]/g, ''));

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
  });
});

addActionHandler('setAuthCode', (global, actions, payload): ActionReturnType => {
  const { code } = payload;

  void callApi('provideAuthCode', code);

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
  });
});

addActionHandler('setAuthPassword', (global, actions, payload): ActionReturnType => {
  const { password } = payload;

  void callApi('provideAuthPassword', password);

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
  });
});

addActionHandler('loginWithPasskey', async (global, actions, payload): Promise<void> => {
  const passkeyOption = global.auth.passkeyOption;
  if (!passkeyOption) return;

  const credential = await navigator.credentials.get(toCredentialRequestOptions(passkeyOption)).catch((e: unknown) => {
    actions.showNotification({
      message: {
        key: 'PasskeyLoginError',
      },
      tabId: getCurrentTabId(),
    });
  });
  if (!credential) return;

  const publicKeyCredential = credential as PublicKeyCredential;
  callApi('restartAuthWithPasskey', publicKeyCredential.toJSON() as AuthenticationResponseJSON);
});

addActionHandler('uploadProfilePhoto', async (global, actions, payload): Promise<void> => {
  const {
    file, isFallback, isVideo, videoTs, bot,
    tabId = getCurrentTabId(),
  } = payload;

  global = updateManagementProgress(global, ManagementProgress.InProgress, tabId);
  setGlobal(global);

  const result = await callApi('uploadProfilePhoto', file, isFallback, isVideo, videoTs, bot);
  if (!result) return;

  global = getGlobal();
  global = updateManagementProgress(global, ManagementProgress.Complete, tabId);
  setGlobal(global);

  const userId = bot?.id ?? global.currentUserId;
  if (!userId) return;

  actions.loadFullUser({ userId });
});

addActionHandler('signUp', (global, actions, payload): ActionReturnType => {
  const { firstName, lastName } = payload;

  void callApi('provideAuthRegistration', { firstName, lastName });

  return updateAuth(global, {
    isLoading: true,
    errorKey: undefined,
  });
});

addActionHandler('returnToAuthPhoneNumber', (global): ActionReturnType => {
  void callApi('restartAuth');

  return updateAuth(global, {
    errorKey: undefined,
  });
});

addActionHandler('goToAuthQrCode', (global): ActionReturnType => {
  void callApi('restartAuthWithQr');

  return updateAuth(global, {
    isLoadingQrCode: true,
    errorKey: undefined,
  });
});

addActionHandler('saveSession', async (global, actions, payload): Promise<void> => {
  if (global.passcode.isScreenLocked) {
    return;
  }

  const { sessionData } = payload;
  if (sessionData) {
    await storeSession(sessionData);
  } else {
    await clearStoredSession();
  }
});

addActionHandler('signOut', async (global, actions, payload): Promise<void> => {
  if ('hangUp' in actions) actions.hangUp({ tabId: getCurrentTabId() });
  if ('leaveGroupCall' in actions) actions.leaveGroupCall({ tabId: getCurrentTabId() });

  try {
    resetInitialLocationHash();
    resetLocationHash();
    await unsubscribe();
    await Promise.race([callApi('destroy'), pause(3000)]);
    await forceWebsync(false);
  } catch (err) {
    // Do nothing
  }

  actions.reset();
  await resetStorage();

  const targetAccountSlot = getFirstLoggedInAccountSlot() || 1;
  if (targetAccountSlot !== (ACCOUNT_SLOT || 1)) {
    window.location.replace(getAccountSlotUrl(targetAccountSlot));
    return;
  }

  if (payload?.forceInitApi) {
    actions.initApi();
  }
});

addActionHandler('requestChannelDifference', (global, actions, payload): ActionReturnType => {
  const { chatId } = payload;

  void callApi('requestChannelDifference', chatId);
});

addActionHandler('reset', async (global, actions): Promise<void> => {
  void cacheApi.clear(MEDIA_CACHE_NAME);
  void cacheApi.clear(MEDIA_CACHE_NAME_AVATARS);
  void cacheApi.clear(MEDIA_PROGRESSIVE_CACHE_NAME);
  void cacheApi.clear(CUSTOM_BG_CACHE_NAME);

  const hasAccounts = await resetStorage();
  destroySharedStatePort();

  const langCachePrefix = LANG_CACHE_NAME.replace(/\d+$/, '');
  const langCacheVersion = Number((LANG_CACHE_NAME.match(/\d+$/) || ['0'])[0]);
  for (let i = 0; i < langCacheVersion; i++) {
    void cacheApi.clear(`${langCachePrefix}${i === 0 ? '' : i}`);
  }

  updateAppBadge(0);

  // Reset auth state to force reauthorization
  global = getGlobal();
  global = {
    ...global,
    currentUserId: undefined,
  };
  global = updateAuth(global, {
    state: undefined,
  });
  setGlobal(global);

  if (hasAccounts) {
    return;
  }

  actions.initShared({ force: true });
  Object.values(global.byTabId).forEach(({ id: otherTabId, isMasterTab }) => {
    actions.init({ tabId: otherTabId, isMasterTab });
  });
});

function resetStorage() {
  if (resetStoragePromise) return resetStoragePromise;

  const hasAccounts = Boolean(Object.values(getAccountsInfo()).length);
  const clearSharedStatePromise = hasAccounts ? Promise.resolve() : removeSharedStateFromCache();

  resetStoragePromise = Promise.all([
    clearStoredSession(ACCOUNT_SLOT),
    clearEncryptedSession(),
    removeGlobalFromCache(),
    clearSharedStatePromise,
  ]).then(() => hasAccounts).finally(() => {
    resetStoragePromise = undefined;
  });

  return resetStoragePromise;
}

addActionHandler('disconnect', (): ActionReturnType => {
  void callApiLocal('disconnect');
});

addActionHandler('destroyConnection', (): ActionReturnType => {
  void callApiLocal('destroy', true, true);
});

addActionHandler('loadNearestCountry', async (global): Promise<void> => {
  if (global.connectionState !== 'connectionStateReady') {
    return;
  }

  const authNearestCountry = await callApi('fetchNearestCountry');

  global = getGlobal();
  global = updateAuth(global, {
    nearestCountry: authNearestCountry,
  });
  setGlobal(global);
});

addActionHandler('setDeviceToken', (global, actions, payload): ActionReturnType => {
  const { token } = payload;
  return {
    ...global,
    push: {
      deviceToken: token,
      subscribedAt: Date.now(),
    },
  };
});

addActionHandler('deleteDeviceToken', (global): ActionReturnType => {
  return {
    ...global,
    push: undefined,
  };
});

addActionHandler('lockScreen', async (global): Promise<void> => {
  const sessionJson = JSON.stringify({ ...await loadStoredSession(), userId: global.currentUserId });
  const globalJson = serializeGlobal(global);
  const sharedStateJson = serializeShared(global.sharedState);

  await encryptSession(sessionJson, globalJson, sharedStateJson);
  forgetPasscode();
  await clearStoredSession();
  updateAppBadge(0);

  global = getGlobal();
  global = updatePasscodeSettings(
    global,
    {
      isScreenLocked: true,
      invalidAttemptsCount: 0,
      timeoutUntil: undefined,
    },
  );
  setGlobal(global);

  setTimeout(() => {
    global = getGlobal();
    global = clearGlobalForLockScreen(global);
    setGlobal(global);
  }, LOCK_SCREEN_ANIMATION_DURATION_MS);

  try {
    await unsubscribe();
    await callApi('destroy', true);
  } catch (err) {
    // Do nothing
  }
});
